import { baseUrlDashboard } from '../../shared/urlApps';
import * as actionTypes from './actionTypes';

/* =========================
   Acciones Punto de Equilibrio
   ========================= */
export const peSetEstado = (estado /* 0=cargando,1=ok,2=error */) => ({
  type: actionTypes.PE_SET_ESTADO,
  estado
});

export const peGuardarDatos = (datos) => ({
  type: actionTypes.PE_GUARDAR_DATOS,
  datos
});

export const peSetError = (mensaje) => ({
  type: actionTypes.PE_SET_ERROR,
  mensaje
});

/* =========================
   Helpers
   ========================= */
const leerRespuesta = async (response) => {
  // Devuelve { ok:boolean, status:number, data:any|string }
  const ct = response.headers.get('content-type') || '';
  if (response.status === 204) return { ok: response.ok, status: response.status, data: null };

  if (ct.includes('application/json')) {
    const data = await response.json();
    return { ok: response.ok, status: response.status, data };
  } else {
    const txt = await response.text().catch(() => '');
    return { ok: response.ok, status: response.status, data: txt };
  }
};

const construirQuery = (paramsObj) => {
  const usp = new URLSearchParams();
  Object.entries(paramsObj).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') usp.append(k, v);
  });
  return usp.toString();
};

// Headers con Bearer desde Redux
const getAuthHeaders = (getState) => {
  const token = getState()?.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/* =========================
   GET: Obtener PuntoDeEquilibrio
   Ejemplo:
   GET ${baseUrlDashboard()}ventas/PuntoDeEquilibrio?mes=8&anio=2025&idMoneda=PES&idEmpresa=114&indicadorId=76
   ========================= */
export const obtenerPuntoDeEquilibrio = ({ mes, anio, idMoneda, idEmpresa, indicadorId }) => {
  return async (dispatch, getState) => {
    dispatch(peSetEstado(0));
    dispatch(peSetError(null));

    const query = construirQuery({ mes, anio, idMoneda, idEmpresa, indicadorId });
    const url = `${baseUrlDashboard()}ventas/PuntoDeEquilibrio?${query}`;

    try {
      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          ...getAuthHeaders(getState)
        }
      });

      const parsed = await leerRespuesta(resp);

      if (!parsed.ok) {
        const detalle =
          typeof parsed.data === 'string'
            ? parsed.data
            : parsed.data?.detail || parsed.data?.message || JSON.stringify(parsed.data);

        // Si falta token, dejemos un mensaje claro
        if (resp.status === 401) {
          dispatch(peSetError('No autorizado (401). Verificá el token en state.auth.token.'));
        } else {
          dispatch(peSetError(`Error ${parsed.status} - ${detalle || 'No se pudo obtener el Punto de Equilibrio'}`));
        }

        dispatch(peSetEstado(2));
        return null;
      }

      dispatch(peGuardarDatos(parsed.data));
      dispatch(peSetEstado(1));
      return parsed.data;
    } catch (e) {
      dispatch(peSetError(e?.message || 'Error de red al obtener Punto de Equilibrio'));
      dispatch(peSetEstado(2));
      return null;
    }
  };
};

/* =========================
   POST: Crear/Actualizar PuntoDeEquilibrio
   Ejemplo:
   POST ${baseUrlDashboard()}ventas/PuntoDeEquilibrio
   BODY:
   {
     "empresaId": 114,
     "idMoneda": "PES",
     "anio": 2025,
     "mes": 8,
     "montoEquilibrio": 347781.27,
     "diaAviso": 24,
     "fechaPuntoDeQuiebreAlcanzado": "2025-08-27T14:46:48.261245"
   }
   ========================= */
export const crearPuntoDeEquilibrio = (payload /* objeto EXACTO del ejemplo */) => {
  return async (dispatch, getState) => {
    dispatch(peSetEstado(0));
    dispatch(peSetError(null));

    const url = `${baseUrlDashboard()}ventas/PuntoDeEquilibrio`;

    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(getState)
        },
        body: JSON.stringify(payload)
      });

      const parsed = await leerRespuesta(resp);

      if (!parsed.ok) {
        const detalle =
          typeof parsed.data === 'string'
            ? parsed.data
            : parsed.data?.errors
            ? Object.values(parsed.data.errors).flat().join('\n')
            : parsed.data?.detail || parsed.data?.message || JSON.stringify(parsed.data);

        if (resp.status === 401) {
          dispatch(peSetError('No autorizado (401). Verificá el token en state.auth.token.'));
        } else {
          dispatch(peSetError(`Error ${parsed.status} - ${detalle || 'No se pudo guardar el Punto de Equilibrio'}`));
        }

        dispatch(peSetEstado(2));
        return;
      }

      if (parsed.data) dispatch(peGuardarDatos(parsed.data));
      dispatch(peSetEstado(1));
    } catch (e) {
      dispatch(peSetError(e?.message || 'Error de red al guardar Punto de Equilibrio'));
      dispatch(peSetEstado(2));
    }
  };
};

/* =========================
   DELETE: Eliminar PuntoDeEquilibrio
   Ejemplo:
   DELETE ${baseUrlDashboard()}ventas/PuntoDeEquilibrio?empresaId=225&idMoneda=DOL&anio=2025&mes=8
   ========================= */
export const eliminarPuntoDeEquilibrio = ({ empresaId, idMoneda, anio, mes }) => {
  return async (dispatch, getState) => {
    dispatch(peSetEstado(0));
    dispatch(peSetError(null));

    const query = construirQuery({ empresaId, idMoneda, anio, mes });
    const url = `${baseUrlDashboard()}ventas/PuntoDeEquilibrio?${query}`;

    try {
      const resp = await fetch(url, {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(getState)
        }
      });

      const parsed = await leerRespuesta(resp);

      if (!parsed.ok) {
        const detalle =
          typeof parsed.data === 'string'
            ? parsed.data
            : parsed.data?.detail || parsed.data?.message || JSON.stringify(parsed.data);

        if (resp.status === 401) {
          dispatch(peSetError('No autorizado (401). Verificá el token en state.auth.token.'));
        } else {
          dispatch(peSetError(`Error ${parsed.status} - ${detalle || 'No se pudo eliminar el Punto de Equilibrio'}`));
        }

        dispatch(peSetEstado(2));
        return;
      }

      // Si se eliminó correctamente, limpiamos los datos en el estado (si querés)
      dispatch(peGuardarDatos(null));
      dispatch(peSetEstado(1));
    } catch (e) {
      dispatch(peSetError(e?.message || 'Error de red al eliminar Punto de Equilibrio'));
      dispatch(peSetEstado(2));
    }
  };
};

/* =========================
   SYNC: ejecutar POSTs y DELETEs pendientes
   posts:  [{ anio, mes, puntoDeEquilibrio, diaDeCorte, idEmpresa, idMoneda, indicadorId }]
   deletes:[{ anio, mes, idEmpresa, idMoneda, indicadorId }]
   reload (opcional): { anio, idMoneda, indicadorId, mes }  // si querés refrescar al final (por defecto mes=12)
   ========================= */
export const syncPuntoDeEquilibrio = (empresaId, posts = [], deletes_ = [], reload = null) => {
  return async (dispatch, getState) => {
    dispatch(peSetEstado(0));
    dispatch(peSetError(null));

    const headersAuth = getAuthHeaders(getState);

    // URLs
    const urlPost = `${baseUrlDashboard()}ventas/PuntoDeEquilibrio`;
    const urlDeleteBase = `${baseUrlDashboard()}ventas/PuntoDeEquilibrio`;

    // Normalizadores de payload
    const buildPostBody = (p) => ({
      empresaId: Number(empresaId ?? p.idEmpresa),
      idMoneda: p.idMoneda,
      anio: p.anio,
      mes: typeof p.mes === 'string' ? parseInt(p.mes, 10) : p.mes,
      montoEquilibrio: p.puntoDeEquilibrio ?? null,
      diaAviso: p.diaDeCorte ?? null
      // Podés agregar fechaPuntoDeQuiebreAlcanzado si tu back lo necesita
      // fechaPuntoDeQuiebreAlcanzado: new Date().toISOString()
    });

    const buildDeleteUrl = (d) => {
      const q = construirQuery({
        empresaId: Number(empresaId ?? d.idEmpresa),
        idMoneda: d.idMoneda,
        anio: d.anio,
        mes: typeof d.mes === 'string' ? parseInt(d.mes, 10) : d.mes
      });
      return `${urlDeleteBase}?${q}`;
    };

    // Ejecutores
    const doPost = async (body) => {
      const resp = await fetch(urlPost, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headersAuth },
        body: JSON.stringify(body)
      });
      const parsed = await leerRespuesta(resp);
      if (!parsed.ok) {
        const detalle =
          typeof parsed.data === 'string'
            ? parsed.data
            : parsed.data?.errors
            ? Object.values(parsed.data.errors).flat().join('\n')
            : parsed.data?.detail || parsed.data?.message || JSON.stringify(parsed.data);
        throw new Error(`POST ${body.anio}-${String(body.mes).padStart(2, '0')}: ${detalle || 'falló'}`);
      }
      return parsed.data ?? true;
    };

    const doDelete = async (url) => {
      const resp = await fetch(url, { method: 'DELETE', headers: { ...headersAuth } });
      const parsed = await leerRespuesta(resp);
      if (!parsed.ok) {
        const detalle =
          typeof parsed.data === 'string'
            ? parsed.data
            : parsed.data?.detail || parsed.data?.message || JSON.stringify(parsed.data);
        const u = new URL(url);
        const anio = u.searchParams.get('anio');
        const mes = u.searchParams.get('mes');
        throw new Error(`DELETE ${anio}-${String(mes).padStart(2, '0')}: ${detalle || 'falló'}`);
      }
      return true;
    };

    // Construcción de promesas
    const postBodies = posts.map(buildPostBody);
    const deleteUrls = deletes_.map(buildDeleteUrl);

    const postPromises = postBodies.map((b) => doPost(b));
    const deletePromises = deleteUrls.map((u) => doDelete(u));

    // Disparamos todo en paralelo
    const resultados = await Promise.allSettled([...postPromises, ...deletePromises]);

    // Armar resumen
    const postedOk = [];
    const postedFail = [];
    const deletedOk = [];
    const deletedFail = [];

    resultados.forEach((r, idx) => {
      const esPost = idx < postBodies.length;
      if (r.status === 'fulfilled') {
        if (esPost) postedOk.push(postBodies[idx]);
        else deletedOk.push(deleteUrls[idx - postBodies.length]);
      } else {
        if (esPost) postedFail.push({ body: postBodies[idx], error: r.reason?.message || String(r.reason) });
        else
          deletedFail.push({ url: deleteUrls[idx - postBodies.length], error: r.reason?.message || String(r.reason) });
      }
    });

    const huboFallas = postedFail.length > 0 || deletedFail.length > 0;

    if (huboFallas) {
      const msgs = [...postedFail.map((f) => `• ${f.error}`), ...deletedFail.map((f) => `• ${f.error}`)];
      dispatch(peSetError(`Algunas operaciones fallaron:\n${msgs.join('\n')}`));
      // Aun con fallas parciales, dejamos el estado en error (2)
      dispatch(peSetEstado(2));
    } else {
      dispatch(peSetEstado(1));
    }

    // (Opcional) Recargar datos al final si se pasó reload
    if (reload && reload.anio && reload.idMoneda) {
      try {
        const { anio, idMoneda, indicadorId, mes } = reload;
        await dispatch(
          obtenerPuntoDeEquilibrio({
            mes: mes ?? 12, // por defecto barrido anual
            anio,
            idMoneda,
            idEmpresa: Number(empresaId),
            indicadorId
          })
        );
      } catch {
        // si falla el refresh, no pisamos el estado anterior
      }
    }

    // Devolvemos un resumen por si el UI quiere mostrar detalle granular
    return {
      ok: !huboFallas,
      summary: {
        postedOk: postedOk.map((b) => ({ anio: b.anio, mes: b.mes })),
        postedFail,
        deletedOk: deletedOk.map((u) => {
          const uu = new URL(u);
          return { anio: Number(uu.searchParams.get('anio')), mes: Number(uu.searchParams.get('mes')) };
        }),
        deletedFail
      }
    };
  };
};
